import React, { useEffect, useRef, useState, type FC } from "react";
import classes from "./ReviewModal.module.css";
import { motion, useAnimationControls } from "framer-motion";
import { useAppDispatch, useAppSelector } from "../Store/hooks";
import { InputText } from "primereact/inputtext";
import {
  useAddMultipleItemsInWatchlistMutation,
  useGetSearchResultsMutation,
  useGetTopResultsQuery,
} from "../Slices/httpSlice";
import { toast } from "react-toastify";
import BackgroundLayout from "./BackgroundLayout";
import { fetchedDataType } from "./Drag-Cards/SectionDragCards";
import MovieCard from "./Drag-Cards/MovieCard";
import SkeletonCardsGrid from "./SkeletonCardsGrid";
import { resetSubWatchList } from "../Slices/watchlistModalSlice";
import { logoutUser } from "../Slices/userSlice";

type WatchlistSubModalProps = {};

const WatchlistSubModal: FC<WatchlistSubModalProps> = () => {
  const controls = useAnimationControls();
  const dispatch = useAppDispatch();

  const [topResults, setTopResults] = useState<[] | fetchedDataType[]>([]);

  const [searchResults, setSearchResults] = useState({
    movies: [],
    series: [],
  });

  const [pageNumber, setPageNumber] = useState<number>(1);

  const [fetchData, setFetchData] = useState<boolean>(false);

  const lastItemRef = useRef<HTMLDivElement | null>(null);

  const [inputValue, setInputValue] = useState("");
  const [typingTimeout, setTypingTimeout] = useState(null);

  const [windowWidth, setWindowWidth] = useState<number>(0);

  const {
    watchlist_sub_list,
    watchlist_id,
    watchlist_selected_items,
    watchlist_items,
  } = useAppSelector((state) => state.watchlistModalSlice);

  const { data, refetch, isFetching, error } = useGetTopResultsQuery(
    {
      page: pageNumber,
    },
    {
      skip: !watchlist_sub_list,
    }
  );

  useEffect(() => {
    if (watchlist_sub_list) {
      controls.start("visible");
      document.body.style.overflow = "hidden";
    }
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [watchlist_sub_list]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        console.log(entries[0]?.isIntersecting, "e");
        if (entries[0]?.isIntersecting && watchlist_sub_list && !fetchData) {
          console.log("enter here and fetch the data ...");
          if (pageNumber <= 16) {
            console.log("yes passed the check page number is less than 7");
            refetch();
            setFetchData(true);
          }
        }
      },
      { threshold: 1.0 }
    );

    if (lastItemRef.current) {
      observer.observe(lastItemRef.current);
    }

    return () => {
      if (lastItemRef.current) {
        observer.unobserve(lastItemRef.current);
      }
    };
  }, [pageNumber, fetchData, watchlist_sub_list, lastItemRef]);

  useEffect(() => {
    if (fetchData && !isFetching) {
      console.log("enter in 1st check...");
      if (watchlist_sub_list && data) {
        console.log(
          "enter here... increase pagenumber and set top results as well..."
        );
        setPageNumber((prevValue) => prevValue + 1);
        setTopResults((prevArray) => {
          return prevArray.length > 0 ? [...prevArray, data] : [data];
        });
        setFetchData(false);
      }
    }
  }, [fetchData, watchlist_sub_list, data, isFetching]);

  useEffect(() => {
    const updateConstraints = () => {
      setWindowWidth(window.innerWidth);
    };

    updateConstraints();
    window.addEventListener("resize", updateConstraints);

    return () => {
      window.removeEventListener("resize", updateConstraints);
    };
  }, []);

  const variants = {
    hidden: {
      y: window.innerHeight,
      display: "none",
    },
    visible: {
      y: 0,
      display: "block",
    },
  };

  const { user } = useAppSelector((state) => state.userSlice);

  const closeWatchlistSubModal = () => {
    controls.start("hidden");
    dispatch(resetSubWatchList(null));
  };

  const [searchApi, { isLoading: searchLoading }] =
    useGetSearchResultsMutation();

  useEffect(() => {
    // Clear previous timeout if user is still typing
    if (typingTimeout) {
      clearTimeout(typingTimeout);
    }

    // Set a new timeout to trigger the API request after 5 seconds of no typing
    const newTimeout: any = setTimeout(() => {
      if (inputValue.trim() !== "") {
        // Call the API after 5 seconds of inactivity
        handleApiRequest();
      }
    }, 1000);

    setTypingTimeout(newTimeout);

    // Cleanup on unmount or when inputValue changes
    return () => clearTimeout(newTimeout);
  }, [inputValue]);

  const handleApiRequest = async () => {
    const response: any = await searchApi({
      body: {
        query: inputValue,
      },
    });
    console.log(response, "response....");

    if (response?.error) {
      console.log(response.error, "error");
      toast.error(
        response?.error?.data?.error?.error ||
          response?.error?.data?.error ||
          response?.error?.data ||
          response?.error?.message ||
          "Something Went Wrong!"
      );
    } else {
      setSearchResults({
        movies: response?.data?.result?.movies,
        series: response?.data?.result?.series,
      });
    }
  };

  const handleInputChange = (e: any) => {
    setInputValue(e.target.value);
  };

  const [addItemsApi, { isLoading: addingItemsLoading }] =
    useAddMultipleItemsInWatchlistMutation();

  const submitHandler = async () => {
    if (watchlist_selected_items.length === 0) {
      toast.info("စောင့်ကြည့်စာရင်းတွင် ထည့်ရန် အနည်းဆုံး အကြောင်းအရာတစ်ခုကို ရွေးပါ။");
    } else {
      const response: any = await addItemsApi({
        body: {
          watchlist_id: watchlist_id,
          items: watchlist_selected_items,
        },
        token: user?.token,
      });

      console.log("response", response);

      if (response?.error) {
        if (response?.error?.status === 403) {
          toast.error("သင့်စက်ရှင် သက်တမ်းကုန်သွားပါပြီ။ ထပ်မံဝင်ရောက်ပါ။");
          dispatch(logoutUser(null));
        } else if (response.error?.data?.error?.status === 0) {
          toast.error("ဤကြည့်ရှုမှုစာရင်းတွင် အကြောင်းအရာကို ထည့်သွင်းပြီးဖြစ်သည်။");
        } else {
          toast.error(
            response?.error?.data?.error?.error ||
            response?.error?.data?.error ||
            response?.error?.data?.message ||
            response?.error?.message ||
            "Something Went Wrong!" 
          );
        }
      } else {
        toast.success("စောင့်ကြည့်စာရင်းတွင် ထည့်သွင်းထားသော အရာများကို အောင်မြင်စွာ ထည့်သွင်းပါ။");
        controls.start("hidden");
        dispatch(resetSubWatchList(null));
      }
    }
  };

  return (
    <motion.div
      className={`${classes.modal}`}
      variants={variants}
      initial="hidden"
      animate={controls}
      transition={{
        duration: 0.5,
        type: "spring",
        damping: 10,
        stiffness: 100,
      }}
    >
      <BackgroundLayout>
        <div className={classes.post_review_navbar}>
          <div className={classes.title_container}>
            <p className={classes.category_title}>
             စောင့်ကြည့်စာရင်းတွင် ထည့်ရန် အရာများကို ရွေးပါ။
            </p>
          </div>
          <div className={classes.btns_container}>
            <button
              className={classes.close_btn}
              type="button"
              onClick={closeWatchlistSubModal}
            >
              <i className={`pi pi-times ${classes.close_icon}`} />
            </button>
            <button
              className={classes.close_btn}
              type="button"
              onClick={submitHandler}
            >
              <i className={`pi pi-check ${classes.close_icon}`} />
            </button>
          </div>
        </div>
        <div className={classes.input_form}>
          <i className={`pi pi-search ${classes.search_icon}`} />
          <InputText
            type="text"
            className={classes.search_input}
            value={inputValue}
            onChange={handleInputChange}
            placeholder="ရုပ်ရှင်များ/စီးရီးများကို ရှာဖွေပါ။"
          />
        </div>

        {watchlist_selected_items.length > 0 && (
          <div className={`${classes.section_drag_cards}`}>
            <div className={classes.section_cards}>
              <motion.div
                className={classes.section_card}
                style={{
                  gridTemplateColumns: `repeat(${watchlist_selected_items.length}, 150px)`,
                }}
                drag="x"
                dragConstraints={{
                  top: 0,
                  left: -(windowWidth <= 800
                    ? 10 +
                      watchlist_selected_items.length * 150 +
                      watchlist_selected_items.length * 10 +
                      watchlist_selected_items.length * 1 -
                      windowWidth
                    : window.innerWidth * 0.05 +
                      watchlist_selected_items.length * 150 +
                      watchlist_selected_items.length * 10 +
                      watchlist_selected_items.length * 1 -
                      windowWidth),
                  right: 0,
                  bottom: 0,
                }}
                transition={{
                  type: "spring",
                  damping: 100,
                  stiffness: 100,
                }}
              >
                {watchlist_selected_items?.map((data, idx) => {
                  return (
                    <MovieCard
                      key={idx}
                      data={data}
                      type="selected titles in wishlist and remove dispatch"
                    />
                  );
                })}
              </motion.div>
            </div>
          </div>
        )}

        <div className={classes.section_drag_cards}>
          <div className={classes.section_cards}>
            <div className={classes.category_title_container}>
              <div className={classes.title_container}>
                <p className={classes.category_title}>ထိပ်တန်းရှာဖွေမှုများ</p>
              </div>
            </div>

            {searchLoading ? (
              <SkeletonCardsGrid />
            ) : (searchResults?.movies.length > 0 ||
                searchResults.series.length > 0) &&
              inputValue.trim().length !== 0 ? (
              <>
                {searchResults?.movies.length > 0 && (
                  <motion.div
                    className={classes.section_card}
                    style={{
                      gridTemplateColumns: `repeat(${searchResults?.movies.length}, 150px)`,
                    }}
                    drag="x"
                    dragConstraints={{
                      top: 0,
                      left: -(windowWidth <= 800
                        ? 10 +
                          searchResults?.movies.length * 150 +
                          searchResults?.movies.length * 10 +
                          searchResults?.movies.length * 1 -
                          windowWidth
                        : window.innerWidth * 0.05 +
                          searchResults?.movies.length * 150 +
                          searchResults?.movies.length * 10 +
                          searchResults?.movies.length * 1 -
                          windowWidth),
                      right: 0,
                      bottom: 0,
                    }}
                    transition={{
                      type: "spring",
                      damping: 100,
                      stiffness: 100,
                    }}
                  >
                    {searchResults?.movies?.map((data, idx) => {
                      return (
                        <MovieCard
                          key={idx}
                          data={data}
                          media_type="movie"
                          watchlist_items={watchlist_items}
                          type="select titles in wishlist and dispatch"
                        />
                      );
                    })}
                  </motion.div>
                )}

                {searchResults?.series.length > 0 && (
                  <motion.div
                    className={classes.section_card}
                    style={{
                      gridTemplateColumns: `repeat(${searchResults?.series.length}, 150px)`,
                    }}
                    drag="x"
                    dragConstraints={{
                      top: 0,
                      left: -(windowWidth <= 800
                        ? 10 +
                          searchResults?.series.length * 150 +
                          searchResults?.series.length * 10 +
                          searchResults?.series.length * 1 -
                          windowWidth
                        : window.innerWidth * 0.05 +
                          searchResults?.series.length * 150 +
                          searchResults?.series.length * 10 +
                          searchResults?.series.length * 1 -
                          windowWidth),
                      right: 0,
                      bottom: 0,
                    }}
                    transition={{
                      type: "spring",
                      damping: 100,
                      stiffness: 100,
                    }}
                  >
                    {searchResults?.series?.map((data, idx) => {
                      return (
                        <MovieCard
                          key={idx}
                          data={data}
                          media_type="tv"
                          watchlist_items={watchlist_items}
                          type="select titles in wishlist and dispatch"
                        />
                      );
                    })}
                  </motion.div>
                )}
              </>
            ) : (
              <>
                {topResults?.map((data, idx) => {
                  return (
                    <motion.div
                      key={idx}
                      className={classes.section_card}
                      style={{
                        gridTemplateColumns: `repeat(${topResults[idx]?.result?.results?.length}, 150px)`,
                      }}
                      drag="x"
                      dragConstraints={{
                        top: 0,
                        left: -(windowWidth <= 800
                          ? 10 +
                            topResults[idx]?.result?.results.length * 150 +
                            topResults[idx]?.result?.results.length * 10 +
                            topResults[idx]?.result?.results.length * 1 -
                            windowWidth
                          : window.innerWidth * 0.05 +
                            topResults[idx]?.result?.results.length * 150 +
                            topResults[idx]?.result?.results.length * 10 +
                            topResults[idx]?.result?.results.length * 1 -
                            windowWidth),
                        right: 0,
                        bottom: 0,
                      }}
                      transition={{
                        type: "spring",
                        damping: 100,
                        stiffness: 100,
                      }}
                    >
                      {data?.result?.results?.map((dataItem, idx2) => (
                        <MovieCard
                          key={`${idx}-${idx2}`}
                          data={dataItem}
                          watchlist_items={watchlist_items}
                          type="select titles in wishlist and dispatch"
                        />
                      ))}
                    </motion.div>
                  );
                })}

                {isFetching && <SkeletonCardsGrid />}
              </>
            )}

            {pageNumber >= 16 ? null : (
              <div ref={lastItemRef} className={classes.load_item}></div>
            )}
          </div>
        </div>
      </BackgroundLayout>
    </motion.div>
  );
};

export default WatchlistSubModal;
